import React from 'react';
import { 
  Activity, 
  Droplet, 
  Heart, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  FileText, 
  MessageSquare,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import './Dashboard.css';
import { getTranslation } from '../config/translations';
import { useLanguage } from '../context/LanguageContext';
import { useGlucose } from '../context/GlucoseContext';
import { useUser } from '../context/UserContext';

interface DashboardProps {
  onNavigate: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onNavigate }) => {
  const { language } = useLanguage();
  const { getAverageGlucose } = useGlucose();
  const { userData } = useUser();

  if (!userData) return null;

  const t = (key: string, params?: Record<string, string>) => getTranslation(language, key, params);
  const recentNotifications = [
    { id: 1, title: 'Upcoming Doctor\'s Appointment', time: '2 hours ago', type: 'info' },
    { id: 2, title: 'Detected High A1C Level', time: '5 hours ago', type: 'alert' },
    { id: 3, title: 'Daily Step Goal Achieved', time: '1 day ago', type: 'success' },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">{t('welcomeBack', { name: userData.personalInfo.firstName })}</h1>
          <p className="dashboard-subtitle">{t('healthOverview')}</p>
        </div>
        <div className="dashboard-date">
          <Calendar size={18} />
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card glucose">
          <div className="metric-icon">
            <Droplet size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Blood Glucose</span>
            <span className="metric-value">{getAverageGlucose()} mg/dL</span>
            <span className="metric-status good">
              <TrendingDown size={14} />
              Within target range
            </span>
          </div>
        </div>

        <div className="metric-card a1c">
          <div className="metric-icon">
            <Activity size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Latest A1C</span>
            <span className="metric-value">6.5%</span>
            <span className="metric-status good">
              <CheckCircle size={14} />
              Excellent control
            </span>
          </div>
        </div>

        <div className="metric-card activity">
          <div className="metric-icon">
            <Activity size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Today's Steps</span>
            <span className="metric-value">7,245</span>
            <span className="metric-status progress">
              <TrendingUp size={14} />
              72% of goal
            </span>
          </div>
        </div>

        <div className="metric-card weight">
          <div className="metric-icon">
            <Heart size={24} />
          </div>
          <div className="metric-content">
            <span className="metric-label">Current Weight</span>
            <span className="metric-value">164 lbs</span>
            <span className="metric-status good">
              <TrendingDown size={14} />
              -4 lbs this month
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2 className="section-title">{t('quickActions')}</h2>
        <div className="actions-grid">
          <button className="action-card" onClick={() => onNavigate('progress')}>
            <Activity size={20} />
            <span>{t('logProgress')}</span>
            <ArrowRight size={16} className="action-arrow" />
          </button>
          <button className="action-card" onClick={() => onNavigate('coach')}>
            <MessageSquare size={20} />
            <span>{t('askHealthCoach')}</span>
            <ArrowRight size={16} className="action-arrow" />
          </button>
          <button className="action-card" onClick={() => onNavigate('documents')}>
            <FileText size={20} />
            <span>{t('viewDocuments')}</span>
            <ArrowRight size={16} className="action-arrow" />
          </button>
          <button className="action-card" onClick={() => onNavigate('charts')}>
            <TrendingUp size={20} />
            <span>View {t('charts')}</span>
            <ArrowRight size={16} className="action-arrow" />
          </button>
        </div>
      </div>

      <div className="dashboard-bottom">
        {/* Recent Activity */}
        <div className="recent-activity">
          <div className="section-header">
            <h2 className="section-title">{t('recentNotifications')}</h2>
            <button className="view-all-btn" onClick={() => onNavigate('notifications')}>
              {t('viewAll')}
              <ArrowRight size={14} />
            </button>
          </div>
          <div className="activity-list">
            {recentNotifications.map((notif) => (
              <div key={notif.id} className={`activity-item ${notif.type}`}>
                <div className="activity-dot"></div>
                <div className="activity-content">
                  <span className="activity-title">{notif.title}</span>
                  <span className="activity-time">{notif.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="upcoming-section">
          <h2 className="section-title">{t('upcomingAppointments')}</h2>
          <div className="appointment-card">
            <div className="appointment-date">
              <span className="date-day">29</span>
              <span className="date-month">Oct</span>
            </div>
            <div className="appointment-details">
              <h3 className="appointment-title">Dr. Michael Smith</h3>
              <p className="appointment-info">{t('quarterlyCheckup')}</p>
              <p className="appointment-time">
                <Calendar size={14} />
                {t('tomorrowAt')} 2:00 PM
              </p>
            </div>
          </div>

          <div className="appointment-card">
            <div className="appointment-date">
              <span className="date-day">02</span>
              <span className="date-month">Nov</span>
            </div>
            <div className="appointment-details">
              <h3 className="appointment-title">Lab Work</h3>
              <p className="appointment-info">{t('quarterlyBloodTests')}</p>
              <p className="appointment-time">
                <Calendar size={14} />
                {t('mondayAt')} 9:00 AM
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
